/**
 * NDC suggestion banner (spec §9.1, patch D7).
 *
 * Renders when `shouldSuggestNdc(project, items)` is true. Shows a
 * dismissable (per-session) banner with an "Issue NDC" button that opens
 * a small modal with a date picker. The PATCH sends `ndcIssuedDate` —
 * server records the date and the user via the existing audit pipeline.
 *
 * The dismissal is per-session — the banner reappears on the next page
 * load. This is intentional: NDC is a real-world action that closes a
 * meaningful chapter of the project, not something a user should be
 * able to permanently hide by accident.
 */
import { useState } from 'react';
import { ScrollText, X } from 'lucide-react';
import { todayPktIsoDate } from '@procurement/shared';
import { Button } from '@/components/ui/button';
import { DmyDateInput } from '@/components/forms/dmy-date-input';
import { FormField } from '@/components/ui/form-field';
import { Modal } from '@/components/ui/modal';
import { shouldSuggestNdc } from '@/lib/ndc-suggestion';
import { useProjectItems } from '@/lib/pipeline-queries';
import { useUpdateProject, type Project } from '@/lib/project-queries';

interface Props {
  project: Project;
}

export function NdcSuggestionBanner({ project }: Props) {
  const items = useProjectItems(project.id);
  const [dismissed, setDismissed] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);

  // Show only when the predicate fires AND the user hasn't dismissed this
  // session. We don't show a skeleton while items load — the banner is a
  // hint, not a primary content surface, so it gracefully appears only
  // once the data is ready.
  if (dismissed) return null;
  if (!items.data) return null;
  if (!shouldSuggestNdc(project, items.data)) return null;

  return (
    <>
      <div
        role="status"
        className="mb-4 flex items-start gap-3 rounded-md border border-info-border bg-info-subtle px-4 py-3"
      >
        <ScrollText className="mt-0.5 h-4 w-4 shrink-0 text-info-fg" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-info-fg">All parts have reached stage 18.</p>
          <p className="mt-0.5 text-xs text-secondary">
            Issue the No-Demand Certificate to mark the project ready for closure.
          </p>
        </div>
        <Button variant="primary" onClick={() => setIssueOpen(true)}>
          Issue NDC
        </Button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="rounded-md p-1 text-secondary hover:bg-info-subtle hover:text-primary"
        >
          <X className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>

      <IssueNdcModal
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        project={project}
      />
    </>
  );
}

function IssueNdcModal({
  open,
  onClose,
  project,
}: {
  open: boolean;
  onClose: () => void;
  project: Project;
}) {
  const today = todayPktIsoDate();
  const [date, setDate] = useState(today);
  const update = useUpdateProject(project.id);

  const submit = (): void => {
    if (!date) return;
    update.mutate(
      { ndcIssuedDate: date, rowVersion: project.rowVersion },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Issue NDC"
      description="Record the date the client issued the No-Demand Certificate."
      size="sm"
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!date}
            loading={update.isPending}
          >
            Save
          </Button>
        </>
      }
    >
      <FormField label="NDC issued on" required htmlFor="ndc-date">
        <DmyDateInput
          id="ndc-date"
          value={date}
          maxDate={today}
          onChange={setDate}
        />
      </FormField>
      {update.error ? (
        <p className="mt-2 text-xs text-danger-fg">{update.error.message}</p>
      ) : null}
    </Modal>
  );
}
